import type { DraftEntry, DraftSet } from './state/loggerDraft';
import type { UseHoldTimer } from './useHoldTimer';
import { kindNeedsDuration } from './deriveEntryKind';

// The start-timer affordance for the active entry — ONE chip now, not two
// (MX-5 D6). It used to be a countdown button next to a stopwatch button plus a
// gear, on a row of their own; the row is gone and the chip lives in the effort
// meter's action column, so the choice it used to ask for has to be deduced
// instead: if a duration exists — typed into the field, or the exercise's
// default — the chip is an hourglass and counts DOWN from it; with nothing to
// count down from it's a stopwatch and counts UP. No new setting to invent: the
// gear still edits the default duration, which is what it already edited, and
// the chip follows it.
export type TimerStartHandlers = {
  /** Which way this entry's timer will run — picks the chip's glyph + label. */
  mode: 'down' | 'up';
  /** Start it. Seeds a countdown from the typed field, else from the default. */
  onStart: () => void;
  /** Open the per-exercise rest+hold defaults sheet (the gear ⚙). */
  onOpenSettings: () => void;
  /** Present only while THIS entry's set is being timed (MX-5 slice 3): the
   *  running count and its commands, which take over the meter's axis row.
   *  Projected straight off `hold` — the timer state has one owner
   *  (useCountTimer) and this is a view of it, never a second copy that could
   *  drift. Absent ⇒ the axis shows the word + gear and the slot the chip. */
  running?: {
    /** What the clock reads — remaining on a countdown, counted on a stopwatch. */
    displaySec: number;
    isPaused: boolean;
    /** Counting down (⇒ ±10 make sense) rather than up. */
    isDown: boolean;
    /** Past the countdown's target and still counting (TM-13). The clock then
     *  reads the OVERSHOOT, which is why the caller prefixes a +: without it
     *  a 12s overrun and 12s left would render as the same "0:12". */
    isOvertime: boolean;
    onAddTen: () => void;
    onSubTen: () => void;
    onFlip: () => void;
    onPauseToggle: () => void;
    onCancel: () => void;
  };
};

/** The ± step of the hold timer, in seconds. The rest timer steps by a minute;
 *  a plank is negotiated in ten-second bites. Written on the buttons, not only
 *  spoken, because the two timers step by different amounts. */
export const HOLD_STEP_SEC = 10;

// Builds the bundle for the active entry, or undefined when there's no timer to
// offer (no bound set, or a reps×weight entry). Extracted from
// TimelineLoggerScreen to keep that render shell under the 500-line cap — and
// it's the natural unit to unit-test the gating + direction + countdown-seed
// rules (TM-5, MX-5 D6). Any duration-logged kind qualifies: cardio blocks
// (TM-5: they had none) and timed isometric holds.
export function buildTimerStartHandlers({
  entry,
  entryIdx,
  boundSet,
  boundSetIdx,
  hold,
  onOpenDefaults,
}: {
  entry: DraftEntry;
  entryIdx: number;
  boundSet: DraftSet | null;
  boundSetIdx: number;
  hold: UseHoldTimer;
  /** Open the per-exercise timer-defaults sheet for a slug (the ⚙). */
  onOpenDefaults: (slug: string) => void;
}): TimerStartHandlers | undefined {
  // Which kinds get a timer is not a question this file answers on its own:
  // "logs a duration rather than reps" already has one owner
  // (kindNeedsDuration), and the swap-warning path in LoggerModals decides the
  // SAME thing from it. Two inline copies of the predicate could drift the day a
  // third duration kind appears, and the drift would be silent — an exercise
  // that warns on swap but offers no timer.
  if (boundSetIdx < 0 || !boundSet || !kindNeedsDuration(entry)) {
    return undefined;
  }
  // The seed the user already typed into the duration field (point B); 0 when
  // the field is empty or garbage.
  const typedSec = parseInt(boundSet.duration, 10) || 0;
  // Same resolution chain the timer itself uses (per-exercise → profile → 0),
  // read off the hook rather than re-derived here.
  const hasDuration = typedSec > 0 || hold.resolveDefaultSec(entryIdx) > 0;
  const mode = hasDuration ? 'down' : 'up';
  // Running only when the timer is on THIS set — while a hold runs the athlete
  // can navigate to another exercise, and that entry's meter must go on showing
  // its own start chip rather than someone else's clock.
  const isRunningHere =
    hold.target?.entryIdx === entryIdx && hold.target?.setIdx === boundSetIdx;
  return {
    mode,
    // A countdown passes the typed seconds as its seed; 0 ⇒ the timer falls
    // back to the per-exercise/global default on its own. A stopwatch ignores
    // the seed entirely.
    onStart: () => hold.start(entryIdx, boundSetIdx, mode, typedSec),
    onOpenSettings: () => onOpenDefaults(entry.exerciseId),
    // Read the count ONLY when it is this set's — the timer state is touched
    // inside the branch so an idle entry never depends on it at all.
    ...(isRunningHere
      ? {
          running: {
            displaySec: hold.timer.state.displaySec,
            isPaused: hold.timer.state.isPaused,
            isDown: hold.timer.state.mode === 'down',
            isOvertime: hold.timer.state.isOvertime,
            onAddTen: () => hold.timer.add(HOLD_STEP_SEC),
            onSubTen: () => hold.timer.add(-HOLD_STEP_SEC),
            onFlip: hold.flipDirection,
            onPauseToggle: hold.timer.state.isPaused
              ? hold.timer.resume
              : hold.timer.pause,
            onCancel: hold.cancel,
          },
        }
      : {}),
  };
}
